import { useContext, useCallback, useMemo } from 'react'
import {
  ShareVersionContext,
  VersioningContext,
  VersioningContextReady,
  ShareWithoutVersion,
} from './ShareVersionContext'
import { VersionFragment } from '@sketch/gql-types'
import {
  getVersionedRoute,
  VersionedRouteKeys,
  RouteParam,
  RouteKeys,
  routes,
} from '@sketch/modules-common'
import { usePreview } from '../../shares/views/PreviewView/PreviewContext'

type PreviewRouteKeys = ExtractSafe<
  RouteKeys,
  | 'PREVIEW_VIEW'
  | 'PREVIEW_PAGE_VIEW'
  | 'PREVIEW_FRAME_VIEW'
  | 'PREVIEW_PROTOTYPES'
  | 'PREVIEW_PROTOTYPE_PLAYER'
>

type GetPathnameKey = ExtractSafe<
  RouteKeys,
  VersionedRouteKeys | PreviewRouteKeys
>

export type GetPathnameProps<Key extends GetPathnameKey> = {
  routeKey: Key
  routeParams: RouteParam<Key>
}

type AnyGetPathnameProps = {
  [Key in GetPathnameKey]: {
    routeKey: Key
    routeParams: RouteParam<Key>
  }
}[GetPathnameKey]

type GetPathname = <Key extends GetPathnameKey>(
  props: GetPathnameProps<Key>
) => string

type PreviewGetPathnameProps = ExtractSafe<
  AnyGetPathnameProps,
  AnyGetPathnameProps & { routeKey: PreviewRouteKeys }
>

type VersionedGetPathnameProps = Exclude<
  AnyGetPathnameProps,
  PreviewGetPathnameProps
>

interface GetVersionPathnameProps {
  versionShortId: string | null
  isViewingLatestVersion: boolean
  routeKey: VersionedGetPathnameProps['routeKey']
  routeParams: VersionedGetPathnameProps['routeParams']
}

const getVersionedPathname = ({
  versionShortId,
  isViewingLatestVersion,
  routeKey,
  routeParams: params,
}: GetVersionPathnameProps) => {
  const route = getVersionedRoute(routeKey, isViewingLatestVersion)

  const routeParams =
    isViewingLatestVersion || !versionShortId
      ? params || {}
      : { ...(params || {}), versionShortId }

  return route.create(routeParams as never)
}

const isPreviewRoute = (
  props: AnyGetPathnameProps
): props is PreviewGetPathnameProps =>
  props.routeKey === 'PREVIEW_VIEW' ||
  props.routeKey === 'PREVIEW_PAGE_VIEW' ||
  props.routeKey === 'PREVIEW_FRAME_VIEW' ||
  props.routeKey === 'PREVIEW_PROTOTYPES' ||
  props.routeKey === 'PREVIEW_PROTOTYPE_PLAYER'

const isVersionContextReady = (
  value: VersioningContext
): value is VersioningContextReady => !value.loading

export const useVersioning = () => {
  const context = useContext(ShareVersionContext)!
  const previewContext = usePreview()

  // Extract data for conditional processing
  const { isViewingLatestVersion, versionShortId, share, latestVersion } =
    context || {}

  const getPathname = useCallback(
    (props: AnyGetPathnameProps) => {
      if (isPreviewRoute(props)) {
        switch (props.routeKey) {
          case 'PREVIEW_VIEW':
            return routes.PREVIEW_VIEW.create(props.routeParams)
          case 'PREVIEW_PAGE_VIEW':
            return routes.PREVIEW_PAGE_VIEW.create(props.routeParams)
          case 'PREVIEW_FRAME_VIEW':
            return routes.PREVIEW_FRAME_VIEW.create(props.routeParams)
          case 'PREVIEW_PROTOTYPES':
            return routes.PREVIEW_PROTOTYPES.create(props.routeParams)
          case 'PREVIEW_PROTOTYPE_PLAYER':
            return routes.PREVIEW_PROTOTYPE_PLAYER.create(props.routeParams)
        }
      }

      if (!context) {
        // For versioned routes without context, we can't generate meaningful paths
        return '/'
      }

      return getVersionedPathname({
        ...props,
        isViewingLatestVersion,
        versionShortId,
      })
    },
    [context, isViewingLatestVersion, versionShortId]
  ) as GetPathname

  const versionIdentifier = useMemo(
    () =>
      context && isVersionContextReady(context)
        ? context.currentVersion.identifier
        : undefined,
    /*
      Since react uses the `Object.is` algorithm, object dependencies would need to
      refer to the same object in memory. Using `currentVersion.identifier` instead.
    */
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [(context as VersioningContextReady)?.currentVersion?.identifier]
  )

  if (!context) {
    // Create minimal valid objects for preview context
    const mockVersion: VersionFragment = {
      __typename: 'Version',
      identifier: '',
      shortId: '',
      kind: 'DRAFT',
      createdAt: '',
      deletedAt: null,
      updatedAt: '',
      description: null,
      userIsCreator: false,
      createdAfterVersioning: false,
      document: null,
      starredAt: null,
      isStarred: false,
      hasComponentManifest: false,
      presentationStatus:
        previewContext?.preview.presentationStatus ?? 'UNKNOWN',
      creator: null,
    } as VersionFragment

    const mockShare: ShareWithoutVersion = {
      __typename: 'Share',
      identifier: '',
      name: previewContext?.preview.name,
      publicAccessLevel: 'NONE' as any,
      userCanEdit: false,
      userCanInspect: false,
      userCanUpdateSettings: false,
      shareType: 'CLOUD_DOCUMENT' as any,
      commentsEnabled: false,
      subscriptionStatus: 'OFF' as any,
      userAccessLevel: 'VIEW' as any,
      project: null,
      workspace: {
        __typename: 'PublicWorkspace',
        identifier: '',
        name: '',
        userIsMember: false,
        userRole: null,
        userIsOwner: false,
        type: 'REGULAR' as any,
        customer: null,
        avatar: null,
      } as any,
      pinnedByCurrentUserAt: null,
    } as ShareWithoutVersion

    // so the useVersioning context typing isn't out of whack when used out of the preview things
    return {
      loading: false,
      share: mockShare,
      latestVersion: mockVersion,
      currentVersion: mockVersion,
      isViewingLatestVersion: true,
      hasPendingPatches: false,
      latestVersionId: '',
      versionShortId: '',
      versionIdentifier,
      getPathname,
      isLibrary: false,
      onVersionUpdateNeeded: async () => {},
      setArtboardThumbnail: () => {},
      artboardThumbnail: '',
      renderStatus: null,
    }
  }

  // Context exists, use the normal return path
  return {
    ...context,
    versionIdentifier,
    getPathname,
    isLibrary: share.shareType === 'LIBRARY',
    latestVersion,
  }
}
